import { useMutation, useQuery } from "@tanstack/react-query";
import { SyntheticEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Footer from "@/components/shared/Footer";
import { DataLoading } from "../../types";
import { VendorUtilities } from "../../utilities/VendorUtilities";
import { Button } from "../shared/Button";
import { Icon } from "../shared/Icon";
import Loader from "../shared/Loader";
import { joinTeam } from "./api/joinTeam";
import { getAvailableTeams } from "./api/teamSelect";

interface TeamSelectionProps {
  handleInviteSignUp: (
    acceptInviteRequest: {
      inviteFromTeamSelection: boolean;
    },
    navigate: ReturnType<typeof useNavigate>,
    inviteSecret: string,
    callback: () => void
  ) => void;
  registerTeam: (
    payload: Record<string, unknown>,
    navigate: ReturnType<typeof useNavigate>
  ) => void;
  dataLoading: DataLoading;
}

const TeamSelection = ({
  handleInviteSignUp,
  registerTeam,
  dataLoading
}: TeamSelectionProps) => {
  const navigate = useNavigate();
  const [inviteLoading, setInviteLoading] = useState(false);
  const { isLoading, data } = useQuery({
    queryKey: ["selectTeam"],
    queryFn: getAvailableTeams
  });
  const {
    isLoading: joinTeamLoading,
    error: joinTeamError,
    mutate,
    data: joinData
  } = useMutation({ mutationFn: joinTeam, retry: 0 });

  const handleInvite = async (inviteSecret: string) => {
    setInviteLoading(true);
    if (!VendorUtilities.isGoogleSignup()) {
      const inviteFromTeamSelectionParam = "?invite_from_team_selection=1";
      navigate(`/invite${inviteFromTeamSelectionParam}#${inviteSecret}`);
      return;
    }

    const acceptInviteRequest = {
      inviteFromTeamSelection: true
    };
    const callback = () => {
      navigate("/apps");
    };
    await handleInviteSignUp(acceptInviteRequest, navigate, inviteSecret, callback);
    window.localStorage.setItem("isAfterInvite", "true");
    setInviteLoading(false);
  };

  const handleJoin = async (e: SyntheticEvent, invite = false) => {
    const element = e.target as HTMLButtonElement;
    if (invite === true) {
      if (inviteLoading) return;
      handleInvite(element.id);
      return;
    } else {
      if (joinTeamLoading) return;
      // else fetch the team and get an invite id, then forward them to the invite accept page
      const payload = {
        team_id: element.id
      };
      mutate(payload);
    }
  };

  const handleCreateTeam = () => {
    const isGoogleSignup = VendorUtilities.isGoogleSignup();
    if (!isGoogleSignup) {
      navigate("/create-team");
      return;
    }
    if (!dataLoading?.registerLoading) {
      registerTeam({}, navigate);
    }
  };

  useEffect(() => {
    if (data?.error?.message === "Unauthorized") {
      // if someone visits this page manually/out of order, redirect to signup
      navigate("/signup");
    }

    // if user has no auto-join or invited teams to join, redirect to create team
    if (
      !isLoading &&
      !data?.invited_teams?.length &&
      !data?.auto_join_enabled_teams?.length
    ) {
      const isGoogleSignup = VendorUtilities.isGoogleSignup();
      if (!isGoogleSignup) {
        navigate("/create-team");
        return;
      }
      registerUser();
    }
  }, [isLoading, data]);

  const registerUser = () => {
    if (!dataLoading?.registerLoading) {
      registerTeam({}, navigate);
    }
  };

  useEffect(() => {
    // if the user has successfully joined a team and gotten an invite id back,
    // handle the invite signup and redirect them
    if (joinData?.invite_id) {
      handleInvite(joinData.invite_id);
      return;
    }
    if (joinTeamError) {
      console.log("Error joining team: ", joinTeamError);
    }
  }, [joinData]);

  // if data is still loading or is null, return null until we can do redirect logic
  if (
    isLoading ||
    (!data?.invited_teams?.length && !data?.auto_join_enabled_teams?.length)
  )
    return null;

  return (
    <div className="flex flex-col w-full bg-page-bg h-full overflow-y-auto">
      <BasicNavBar />
      <div className="flex flex-1 justify-center items-center w-full">
        <div className="flex flex-col max-w-[400px] items-center gap-5 py-9 mx-3 h-full box-border">
          <h1 className="leading-[1.4] text-[36px] font-['Poppins'] mb-2.5 text-gray-800 text-center">
            Create or join a team
          </h1>
          {data?.invited_teams?.length > 0 && (
            <>
              <h5 className="font-semibold text-lg leading-[28px] text-gray-600">You have been invited to join a team</h5>
              {data.invited_teams
                .sort((a, b) => {
                  return a.user_names.length < b.user_names.length;
                })
                .map(team => {
                  return (
                    <div key={team.id} className="bg-white shadow-[0px_1px_6px_rgba(0,0,0,0.25)] p-[15px] flex flex-wrap rounded w-full box-border">
                      <div className="flex justify-between w-full">
                        <div className="flex flex-col gap-[6px]">
                          <p className="text-base text-gray-600 leading-[22px] font-semibold">{team.name}</p>
                        </div>
                        {joinTeamLoading ? (
                          <Loader size="30" />
                        ) : (
                          <Button
                            kind="primary-purple"
                            id={team.invite_id}
                            onClick={e => handleJoin(e, true)}
                          >
                            Join
                          </Button>
                        )}
                      </div>
                      <div className="w-full text-sm font-medium mt-2">{team.user_names.length} members</div>
                    </div>
                  );
                })}
            </>
          )}
          {data?.auto_join_enabled_teams?.length > 0 && (
            <>
              <h5 className="font-semibold text-lg leading-[28px] text-gray-600">
                {data?.auto_join_enabled_teams?.length === 1
                  ? "We found a team that you are able to join"
                  : "We found teams that you are able to join"}
              </h5>
              {data.auto_join_enabled_teams
                .sort((a, b) => {
                  return a.user_names.length < b.user_names.length;
                })
                .map(team => {
                  return (
                    <div key={team.id} className="bg-white shadow-[0px_1px_6px_rgba(0,0,0,0.25)] p-[15px] flex flex-wrap rounded w-full box-border">
                      <div className="flex justify-between w-full">
                        <div className="flex flex-col gap-[6px]">
                          <p className="text-base text-gray-600 leading-[22px] font-semibold">{team.name}</p>
                        </div>
                        {joinTeamLoading ? (
                          <Loader size="30" />
                        ) : (
                          <Button
                            kind="primary-purple"
                            id={team.id}
                            onClick={e => handleJoin(e)}
                          >
                            Join
                          </Button>
                        )}
                      </div>
                      <div className="w-full text-sm font-medium mt-2">{team.user_names.length} members</div>
                    </div>
                  );
                })}
            </>
          )}
          <div className="w-full flex text-hex-bebebe items-center gap-[10px] text-xl font-semibold leading-[20px] my-[10px]">
            <span className="inline-block grow h-px bg-hex-bebebe" />
            or
            <span className="inline-block grow h-px bg-hex-bebebe" />
          </div>
          {dataLoading?.registerLoading ? (
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden justify-center items-center">
              <Loader size="48" />
            </div>
          ) : (
            <Button kind="primary-purple" size="large" onClick={handleCreateTeam}>
              Create a new team
            </Button>
          )}
        </div>
      </div>
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

const BasicNavBar = () => (
  <header className="w-full flex flex-col">
    <div className="w-full relative bg-white shadow-[0_1px_4px_rgba(0,0,0,0.16)] z-[400] px-5 py-3 box-border flex flex-1 justify-start items-center">
      <div className="mr-5 relative">
        <Link to="/" className="flex flex-row" aria-label="Replicated home">
          <Icon name="replicatedLogo" width="150" height="34" />
        </Link>
      </div>
    </div>
  </header>
);

export default TeamSelection;
